import React, { useState, useEffect, useMemo, useCallback } from "react";
import api from "services/api";
import Header from "components/Header";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

const formatDateToDDMMYYYY = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const convertToYYYYMMDD = (dateString) => {
  if (!dateString) return "";
  const [day, month, year] = String(dateString).split("-");
  if (!day || !month || !year) return "";
  return `${year}-${month}-${day}`;
};

const parseFrenchDate = (dateStr) => {
  if (!dateStr) return null;
  const [day, month, year] = String(dateStr).split("-").map(Number);
  if (!day || !month || !year) return null;
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
};

const safeNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const normalized = String(value).replace(",", ".").trim();
  const num = Number(normalized);
  return Number.isNaN(num) ? null : num;
};

const getStatusLabel = (status) => {
  if (status === "VALIDE") return "Valide";
  if (status === "ANOMALIE") return "Anomalie";
  if (status === "PUBLIE") return "Publié";
  return status || "-";
};

const FALLBACK_VILLAGE_ORDER = [
  "Thiary Thieurigne",
  "Thiary Loro",
  "Maka Sarr",
  "Santhiou Diokoul",
  "Ndam Ndam Peulh",
  "Ndam Ndam Ouolof",
  "Taiba Ndiouga",
  "Guedj Seck",
  "Dialgane Guedj",
  "Bakhdar Ouolof",
  "Bakhdar Peulh2",
  "Bakhdar Peulh1",
  "Bahis Seck",
  "Bahis Peulh",
  "Merina Dia",
  "Ngaraf Peulh",
  "Darou Ngaraf",
  "Darou Niang",
  "Ngaraf Ouolof",
  "Bandegne Fass",
  "Bandegne Peulh",
  "Wawlou Sall",
  "Nguembe Dieng",
  "Maka Sall",
  "Celle Diagne",
  "Darou Mbengue",
  "Pallene 1, 2 et Peulh",
  "Racine Peulh",
  "Khara Ndack",
  "Keur Bakary",
  "Gouye Mbaye",
  "Keur Medoune",
  "Siwal Peulh",
  "Dara Khara",
];

const ControleImport = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery("(min-width:1200px)");
  const isTablet = useMediaQuery("(min-width:768px)");

  const [villages, setVillages] = useState(FALLBACK_VILLAGE_ORDER);
  const [village, setVillage] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const [imageFile, setImageFile] = useState(null);
  const [ocrText, setOcrText] = useState("");
  const [rows, setRows] = useState([]);
  const [displayRows, setDisplayRows] = useState([]);
  const [batchId, setBatchId] = useState(null);

  const [loading, setLoading] = useState(false);
  const [pageError, setPageError] = useState("");
  const [pageSuccess, setPageSuccess] = useState("");
  const [noResultsMessage, setNoResultsMessage] = useState("");

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmMode, setConfirmMode] = useState("control");
  const [confirmationCode, setConfirmationCode] = useState("");

  const [warnings, setWarnings] = useState([]);
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");

  useEffect(() => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    if (!selectedDate) setSelectedDate(formatDateToDDMMYYYY(firstDay));
    if (!startDate) setStartDate(formatDateToDDMMYYYY(firstDay));
    if (!endDate) setEndDate(formatDateToDDMMYYYY(lastDay));
  }, [selectedDate, startDate, endDate]);

  useEffect(() => {
    const fetchVillages = async () => {
      try {
        const res = await api.get("/api/villages/ordered");
        const normalized = Array.isArray(res.data)
          ? res.data
              .map((item) =>
                typeof item === "string"
                  ? item.trim()
                  : String(item?.village || "").trim()
              )
              .filter(Boolean)
          : [];

        setVillages(normalized.length ? normalized : FALLBACK_VILLAGE_ORDER);
      } catch (error) {
        console.error("Erreur chargement villages:", error);
        setVillages(FALLBACK_VILLAGE_ORDER);
      }
    };

    fetchVillages();
  }, []);

  const applyFilters = useCallback(() => {
    let filtered = [...rows];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter((item) => {
        return (
          String(item.paperClientId || "").toLowerCase().includes(q) ||
          String(item.paperFullName || "").toLowerCase().includes(q) ||
          String(item.waterBillRefNumber || "").toLowerCase().includes(q) ||
          String(item.firstName || "").toLowerCase().includes(q) ||
          String(item.lastName || "").toLowerCase().includes(q) ||
          String(item.anomalyType || "").toLowerCase().includes(q)
        );
      });
    }

    const start = startDate ? parseFrenchDate(startDate) : null;
    const end = endDate ? parseFrenchDate(endDate) : null;

    filtered = filtered.filter((item) => {
      const itemDate = parseFrenchDate(item.selectedDate);
      if (!itemDate) return true;
      if (start && itemDate < start) return false;
      if (end && itemDate > end) return false;
      return true;
    });

    if (statusFilter) {
      filtered = filtered.filter((item) => item.controlStatus === statusFilter);
    }

    if (village) {
      filtered = filtered.filter((item) => item.village === village);
    }

    setDisplayRows(filtered);
    setNoResultsMessage(
      filtered.length === 0
        ? "Aucune donnée trouvée avec les filtres appliqués."
        : ""
    );
  }, [rows, searchQuery, startDate, endDate, statusFilter, village]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const stats = useMemo(() => {
    let ok = 0;
    let anomalies = 0;
    let published = 0;
    let matched = 0;

    rows.forEach((row) => {
      if (row.controlStatus === "VALIDE") ok += 1;
      if (row.controlStatus === "ANOMALIE") anomalies += 1;
      if (row.controlStatus === "PUBLIE") published += 1;
      if (row.isMatched) matched += 1;
    });

    return {
      total: rows.length,
      ok,
      anomalies,
      published,
      matched,
      unmatched: rows.length - matched,
    };
  }, [rows]);

  const recomputeLocalRow = (row) => {
    const currentIndex = safeNumber(row.currentIndex);
    const previousIndex = safeNumber(row.previousIndex);
    const consommation = safeNumber(row.consommation);
    const consommationCalc =
      currentIndex !== null && previousIndex !== null
        ? currentIndex - previousIndex
        : null;

    const errors = [];

    if (!row.clientId || !row.waterBillRefNumber) {
      errors.push("CLIENT_NOT_FOUND");
    }

    if (
      currentIndex !== null &&
      previousIndex !== null &&
      currentIndex < previousIndex
    ) {
      errors.push("INDEX_ORDER_ERROR");
    }

    if (
      currentIndex !== null &&
      previousIndex !== null &&
      consommation !== null &&
      Math.abs(consommationCalc - consommation) > 0.001
    ) {
      errors.push("CONS_MISMATCH");
    }

    if (row.alreadyExistsForMonth) {
      errors.push("ALREADY_BILLED");
    }

    return {
      ...row,
      currentIndex,
      previousIndex,
      consommation,
      duePayment: safeNumber(row.duePayment),
      consommationCalc,
      controlStatus: errors.length ? "ANOMALIE" : "VALIDE",
      anomalyType: errors.length ? errors.join("|") : null,
      anomalyDetails: errors.length ? errors.join(", ") : null,
    };
  };

  const handleScanImage = async () => {
    setPageError("");
    setPageSuccess("");

    if (!village || !selectedDate || !imageFile) {
      setPageError("Village, date du batch et image sont requis.");
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("village", village);
      formData.append("selectedDate", selectedDate);
      formData.append("file", imageFile);

      const res = await api.post(
        "/api/consommations-control/scan-image",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      const preparedRows = (res.data.rows || []).map((r, index) =>
        recomputeLocalRow({
          id: r.id || `preview-${index + 1}`,
          ...r,
        })
      );

      setRows(preparedRows);
      setBatchId(null);
      setOcrText(res.data.rawText || "");
      setWarnings(Array.isArray(res.data.warnings) ? res.data.warnings : []);
      setImagePreviewUrl(URL.createObjectURL(imageFile));
      setPageSuccess("Image analysée avec succès. Vérifie les lignes avant enregistrement.");
    } catch (err) {
      setPageError(err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRowUpdate = async (newRow) => {
    const updatedRow = recomputeLocalRow(newRow);

    setRows((prev) =>
      prev.map((row) => (row.id === updatedRow.id ? updatedRow : row))
    );

    if (batchId && typeof updatedRow.id === "number") {
      try {
        const res = await api.put(
          `/api/consommations-control/${updatedRow.id}`,
          {
            clientId: updatedRow.clientId,
            waterBillRefNumber: updatedRow.waterBillRefNumber,
            firstName: updatedRow.firstName,
            lastName: updatedRow.lastName,
            currentIndex: updatedRow.currentIndex,
            previousIndex: updatedRow.previousIndex,
            consommation: updatedRow.consommation,
            duePayment: updatedRow.duePayment,
            selectedDate: updatedRow.selectedDate,
          }
        );

        const normalized = recomputeLocalRow({
          ...res.data,
          id: res.data.id,
        });

        setRows((prev) =>
          prev.map((row) => (row.id === normalized.id ? normalized : row))
        );

        return normalized;
      } catch (err) {
        throw new Error(err?.response?.data?.message || err.message);
      }
    }

    return updatedRow;
  };

  const handleProcessRowUpdateError = (error) => {
    setPageError(error?.message || "Erreur lors de la mise à jour.");
  };

  const openConfirmDialog = (mode) => {
    setConfirmMode(mode);
    setConfirmationCode("");
    setConfirmOpen(true);
  };

  const handleSaveToControl = async () => {
    setPageError("");
    setPageSuccess("");

    try {
      const res = await api.post("/api/consommations-control/save-preview", {
        rows,
        village,
        selectedDate,
        sourceFileName: imageFile?.name || null,
      });

      const savedRows = (res.data.rows || []).map((r) =>
        recomputeLocalRow({ ...r, id: r.id })
      );

      setBatchId(res.data.batchId || null);
      setRows(savedRows);
      setPageSuccess(res.data.message || "Enregistré dans consommationsControl.");
      setConfirmOpen(false);
    } catch (err) {
      setPageError(err?.response?.data?.message || err.message);
    }
  };

  const handleDirectSave = async () => {
    setPageError("");
    setPageSuccess("");

    try {
      const res = await api.post("/api/consommations-control/save-direct", {
        rows,
        confirmationCode,
      });

      setPageSuccess(res.data.message || "Lignes versées directement dans consommations.");
      setConfirmOpen(false);
      setConfirmationCode("");
    } catch (err) {
      setPageError(err?.response?.data?.message || err.message);
    }
  };

  const handlePublishSavedBatch = async () => {
    setPageError("");
    setPageSuccess("");

    if (!batchId) {
      setPageError("Aucun batch enregistré à publier.");
      return;
    }

    try {
      const res = await api.post(
        `/api/consommations-control/publish/${batchId}`,
        {
          confirmationCode,
          publishedBy: "admin",
        }
      );

      const refreshed = await api.get(
        `/api/consommations-control/batch/${batchId}`
      );

      const refreshedRows = (refreshed.data || []).map((r) =>
        recomputeLocalRow({ ...r, id: r.id })
      );

      setRows(refreshedRows);
      setPageSuccess(res.data.message || "Batch publié avec succès.");
      setConfirmOpen(false);
      setConfirmationCode("");
    } catch (err) {
      setPageError(err?.response?.data?.message || err.message);
    }
  };

  const handleConfirmAction = async () => {
    if (confirmMode === "control") {
      await handleSaveToControl();
      return;
    }

    if (confirmMode === "direct") {
      await handleDirectSave();
      return;
    }

    if (confirmMode === "publish") {
      await handlePublishSavedBatch();
    }
  };

  const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(url);
};

  const exportRows = async (format) => {
    try {
      const res = await api.post(
        `/api/consommations-control/export/${format}`,
        { rows },
        { responseType: "blob" }
      );

      const fileNames = {
        csv: "controle-import.csv",
        xlsx: "controle-import.xlsx",
        pdf: "controle-import.pdf",
      };

      downloadBlob(res.data, fileNames[format]);
    } catch (err) {
      setPageError("Erreur lors de l'export.");
    }
  };

  const columns = [
    {
      field: "paperClientId",
      headerName: "ID Cahier",
      minWidth: 110,
      editable: true,
    },
    {
      field: "paperFullName",
      headerName: "Nom Cahier",
      minWidth: 180,
      editable: true,
    },
    {
      field: "waterBillRefNumber",
      headerName: "Référence",
      minWidth: 150,
      editable: true,
    },
    {
      field: "selectedDate",
      headerName: "Date",
      minWidth: 115,
    },
    {
      field: "firstName",
      headerName: "Prénom",
      minWidth: 130,
      editable: true,
    },
    {
      field: "lastName",
      headerName: "Nom",
      minWidth: 130,
      editable: true,
    },
    {
      field: "currentIndex",
      headerName: "Nouveau",
      minWidth: 110,
      type: "number",
      editable: true,
    },
    {
      field: "previousIndex",
      headerName: "Ancien",
      minWidth: 110,
      type: "number",
      editable: true,
    },
    {
      field: "consommation",
      headerName: "Cons écrit",
      minWidth: 120,
      type: "number",
      editable: true,
    },
    {
      field: "consommationCalc",
      headerName: "Cons calcul",
      minWidth: 120,
      type: "number",
    },
    {
      field: "duePayment",
      headerName: "Montant",
      minWidth: 120,
      editable: true,
      renderCell: (params) =>
        params.value === null || params.value === undefined
          ? "-"
          : `${Number(params.value).toFixed(2)} CFA`,
    },
    {
      field: "controlStatus",
      headerName: "Statut",
      minWidth: 120,
      renderCell: (params) => {
        const status = params.value;
        const color =
          status === "VALIDE"
            ? "success"
            : status === "PUBLIE"
            ? "info"
            : "error";

        return (
          <Chip
            size="small"
            color={color}
            label={getStatusLabel(status)}
            variant="outlined"
          />
        );
      },
    },
    {
      field: "anomalyType",
      headerName: "Anomalie",
      minWidth: 220,
    },
    {
      field: "alreadyExistsForMonth",
      headerName: "Déjà facturé",
      minWidth: 120,
      renderCell: (params) => (params.value ? "Oui" : "Non"),
    },
    {
      field: "confidence",
      headerName: "Confiance",
      minWidth: 110,
      renderCell: (params) =>
        params.value === null || params.value === undefined
          ? "-"
          : `${Math.round(Number(params.value) * 100)}%`,
    },
  ];

  const canSavePreview = rows.length > 0;
  const canDirectSave =
    rows.length > 0 && rows.every((r) => r.controlStatus === "VALIDE");
  const canPublishBatch =
    !!batchId && rows.length > 0 && rows.every((r) => r.controlStatus === "VALIDE");

  return (
    <Box m={{ xs: "1rem", md: "1.5rem 2rem" }}>
      <Box
        display="flex"
        flexDirection={{ xs: "column", xl: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", xl: "center" }}
        gap={3}
        mb={3}
      >
        <Header
          title="CONTRÔLE IMPORT"
          subtitle="Image du cahier → prévisualisation → correction → sauvegarde"
        />

        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: { xs: "100%", xl: "1040px" },
            p: 2,
            borderRadius: "1.5rem",
            backgroundColor: theme.palette.background.alt,
          }}
        >
          <Box
            display="grid"
            gridTemplateColumns={{
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              lg: "repeat(4, minmax(0, 1fr))",
            }}
            gap={2}
          >
            <TextField
              fullWidth
              label="Recherche"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />

            <TextField
              fullWidth
              type="date"
              label="Start Date"
              value={convertToYYYYMMDD(startDate)}
              onChange={(e) => setStartDate(formatDateToDDMMYYYY(e.target.value))}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              fullWidth
              type="date"
              label="End Date"
              value={convertToYYYYMMDD(endDate)}
              onChange={(e) => setEndDate(formatDateToDDMMYYYY(e.target.value))}
              InputLabelProps={{ shrink: true }}
            />

            <Autocomplete
              options={villages}
              value={village}
              onChange={(event, newValue) => setVillage(newValue || "")}
              renderInput={(params) => (
                <TextField {...params} label="Village" fullWidth />
              )}
            />

            <TextField
              fullWidth
              type="date"
              label="Date du batch"
              value={convertToYYYYMMDD(selectedDate)}
              onChange={(e) =>
                setSelectedDate(formatDateToDDMMYYYY(e.target.value))
              }
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              fullWidth
              select
              label="Statut"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">Tous</option>
              <option value="VALIDE">VALIDE</option>
              <option value="ANOMALIE">ANOMALIE</option>
              <option value="PUBLIE">PUBLIE</option>
            </TextField>

            <Button
              fullWidth
              variant="outlined"
              component="label"
              sx={{ minHeight: 56 }}
            >
              {imageFile?.name ? "Image sélectionnée" : "Choisir image"}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => setImageFile(e.target.files?.[0] || null)}
              />
            </Button>

            <Button
              fullWidth
              variant="contained"
              onClick={handleScanImage}
              disabled={loading}
              sx={{ minHeight: 56 }}
            >
              Analyser image
            </Button>
          </Box>
        </Paper>
      </Box>

      {!!pageError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: "1rem" }}>
          {pageError}
        </Alert>
      )}

      {!!pageSuccess && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: "1rem" }}>
          {pageSuccess}
        </Alert>
      )}

      <Box
        display="grid"
        gridTemplateColumns={{
          xs: "1fr",
          xl: "minmax(0, 2.35fr) minmax(320px, 0.9fr)",
        }}
        gap={3}
        alignItems="start"
      >
        <Box minWidth={0}>
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              borderRadius: "1.5rem",
              backgroundColor: theme.palette.background.alt,
              overflow: "hidden",
            }}
          >
            <Box sx={{ width: "100%", overflowX: "auto" }}>
              <Box sx={{ minWidth: 1680, height: { xs: 520, md: 640 } }}>
                <DataGrid
                  rows={displayRows}
                  columns={columns}
                  loading={loading}
                  getRowId={(row) => row.id}
                  editMode="row"
                  processRowUpdate={handleRowUpdate}
                  onProcessRowUpdateError={handleProcessRowUpdateError}
                  disableRowSelectionOnClick
                  pageSizeOptions={[25, 50, 100]}
                  initialState={{
                    pagination: {
                      paginationModel: { pageSize: 25, page: 0 },
                    },
                  }}
                  getRowClassName={(params) => {
                    if (params.row.controlStatus === "PUBLIE") return "row-published";
                    if (params.row.controlStatus === "VALIDE") return "row-ok";
                    return "row-error";
                  }}
                  slots={{
                    noRowsOverlay: () => (
                      <Box
                        sx={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: theme.palette.secondary[200],
                          textAlign: "center",
                          px: 3,
                        }}
                      >
                        <Typography variant="h6">
                          {noResultsMessage || "Aucune donnée disponible"}
                        </Typography>
                      </Box>
                    ),
                  }}
                  sx={{
                    border: "none",
                    "& .MuiDataGrid-columnHeaders": {
                      backgroundColor: theme.palette.background.alt,
                      color: theme.palette.secondary[100],
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    },
                    "& .MuiDataGrid-cell": {
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    },
                    "& .MuiDataGrid-footerContainer": {
                      borderTop: "none",
                      backgroundColor: theme.palette.background.alt,
                    },
                    "& .row-ok": {
                      backgroundColor: "rgba(56, 142, 60, 0.10)",
                    },
                    "& .row-error": {
                      backgroundColor: "rgba(211, 47, 47, 0.10)",
                    },
                    "& .row-published": {
                      backgroundColor: "rgba(25, 118, 210, 0.10)",
                    },
                  }}
                />
              </Box>
            </Box>
          </Paper>
        </Box>

        <Box
          display="grid"
          gap={3}
          gridTemplateColumns={{
            xs: "1fr",
            md: !isDesktop && isTablet ? "1fr 1fr" : "1fr",
          }}
        >
          <InfoCard
            title="Résumé du batch"
            lines={[
              `Total lignes: ${stats.total}`,
              `Valides: ${stats.ok}`,
              `Anomalies: ${stats.anomalies}`,
              `Publiées: ${stats.published}`,
              `Matchées: ${stats.matched}`,
              `Non matchées: ${stats.unmatched}`,
              `Batch: ${batchId || "-"}`,
              `Fichier image: ${imageFile?.name || "-"}`,
            ]}
          />

          <ActionCard
            title="Actions"
            subtitle="Choisis le mode d’enregistrement après vérification."
          >
            <Stack spacing={1.5}>
              <Button
                fullWidth
                variant="contained"
                onClick={() => openConfirmDialog("control")}
                disabled={!canSavePreview}
              >
                Enregistrer dans consommationsControl
              </Button>

              <Button
                fullWidth
                color="success"
                variant="contained"
                onClick={() => openConfirmDialog("direct")}
                disabled={!canDirectSave}
              >
                Verser directement dans consommations
              </Button>

              <Button
                fullWidth
                color="info"
                variant="contained"
                onClick={() => openConfirmDialog("publish")}
                disabled={!canPublishBatch}
              >
                Publier le batch enregistré
              </Button>
            </Stack>
            <Stack spacing={1.5}>
              <Button
                fullWidth
                variant="contained"
                onClick={() => openConfirmDialog("control")}
                disabled={!canSavePreview}
              >
                Enregistrer dans consommationsControl
              </Button>

              <Button
                fullWidth
                color="success"
                variant="contained"
                onClick={() => openConfirmDialog("direct")}
                disabled={!canDirectSave}
              >
                Verser directement dans consommations
              </Button>

              <Button
                fullWidth
                color="info"
                variant="contained"
                onClick={() => openConfirmDialog("publish")}
                disabled={!canPublishBatch}
              >
                Publier le batch enregistré
              </Button>

              <Button fullWidth variant="outlined" onClick={() => exportRows("csv")} disabled={!rows.length}>
                Export CSV
              </Button>

              <Button fullWidth variant="outlined" onClick={() => exportRows("xlsx")} disabled={!rows.length}>
                Export Excel
              </Button>

              <Button fullWidth variant="outlined" onClick={() => exportRows("pdf")} disabled={!rows.length}>
                Export PDF
              </Button>
            </Stack>
          </ActionCard>

          <ActionCard
            title="Texte OCR"
            subtitle="Aide au contrôle visuel si la lecture automatique paraît incorrecte."
          >
            <Box
              sx={{
                p: 1.5,
                minHeight: 150,
                maxHeight: 250,
                overflow: "auto",
                borderRadius: "1rem",
                bgcolor: "rgba(255,255,255,0.03)",
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography
                variant="body2"
                sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
              >
                {ocrText || "Aucun texte OCR pour le moment."}
              </Typography>
            </Box>
          </ActionCard>
          <ActionCard
            title="Image source"
            subtitle="Vérification visuelle de la lecture automatique."
          >
            <Box
              sx={{
                p: 1.5,
                minHeight: 180,
                borderRadius: "1rem",
                bgcolor: "rgba(255,255,255,0.03)",
                border: `1px solid ${theme.palette.divider}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
              }}
            >
              {imagePreviewUrl ? (
                <Box
                  component="img"
                  src={imagePreviewUrl}
                  alt="Aperçu source"
                  sx={{ width: "100%", maxHeight: 320, objectFit: "contain" }}
                />
              ) : (
                <Typography variant="body2">Aucune image chargée.</Typography>
              )}
            </Box>
          </ActionCard>

          <ActionCard
            title="Avertissements OCR"
            subtitle="Messages utiles si certaines lignes n'ont pas été bien lues."
          >
            <Box
              sx={{
                p: 1.5,
                minHeight: 120,
                maxHeight: 220,
                overflow: "auto",
                borderRadius: "1rem",
                bgcolor: "rgba(255,255,255,0.03)",
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              {warnings.length ? (
                warnings.map((w, i) => (
                  <Typography key={i} variant="body2" sx={{ mb: 1 }}>
                    - {w}
                  </Typography>
                ))
              ) : (
                <Typography variant="body2">Aucun avertissement.</Typography>
              )}
            </Box>
          </ActionCard>
        </Box>
      </Box>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {confirmMode === "control" && "Enregistrer en contrôle"}
          {confirmMode === "direct" && "Versement direct"}
          {confirmMode === "publish" && "Publier le batch"}
        </DialogTitle>

        <DialogContent>
          <Typography mb={2}>
            {confirmMode === "control" &&
              "Les lignes actuelles seront enregistrées dans consommationsControl."}
            {confirmMode === "direct" &&
              "Les lignes valides seront versées directement dans consommations."}
            {confirmMode === "publish" &&
              "Le batch déjà enregistré sera publié dans consommations."}
          </Typography>

          {(confirmMode === "direct" || confirmMode === "publish") && (
            <TextField
              fullWidth
              label="Code de confirmation"
              value={confirmationCode}
              onChange={(e) => setConfirmationCode(e.target.value)}
            />
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleConfirmAction}>
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const InfoCard = ({ title, lines }) => {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: "1.75rem",
        backgroundColor: theme.palette.background.alt,
        minWidth: 0,
      }}
    >
      <Typography variant="h5" mb={2}>
        {title}
      </Typography>

      <Stack spacing={1.2}>
        {lines.map((line, idx) => (
          <Typography
            key={idx}
            variant="body1"
            sx={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
          >
            {line}
          </Typography>
        ))}
      </Stack>
    </Paper>
  );
};

const ActionCard = ({ title, subtitle, children }) => {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: "1.75rem",
        backgroundColor: theme.palette.background.alt,
        minWidth: 0,
      }}
    >
      <Typography variant="h5" mb={1}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body2" mb={2.5} color="text.secondary">
          {subtitle}
        </Typography>
      )}
      {children}
    </Paper>
  );
};

export default ControleImport;

